/**
 * Welcome text placeholders for event start screen.
 * Placeholders are replaced with locale-formatted date, time, and location
 * on the Create Event preview and on the Registration screen.
 */
export const PLACEHOLDER_DATE_RANGE = '{{DATE_RANGE}}';
export const PLACEHOLDER_TIME_RANGE = '{{TIME_RANGE}}';
export const PLACEHOLDER_LOCATION = '{{LOCATION}}';
/** Default welcome template shown when creating a new event. */
export const DEFAULT_WELCOME_TEMPLATE = `<p>${PLACEHOLDER_LOCATION}</p><p>${PLACEHOLDER_DATE_RANGE}</p><p>${PLACEHOLDER_TIME_RANGE}</p><p>Welcome to this event</p>`;
export function getDefaultWelcomeTemplate(t) {
    return `<p>${PLACEHOLDER_LOCATION}</p><p>${PLACEHOLDER_DATE_RANGE}</p><p>${PLACEHOLDER_TIME_RANGE}</p><p>${t('events.createNewEvent.defaultWelcomeMessage', 'Welcome to this event')}</p>`;
}
export function getDefaultThankYouText(t) {
    return t('events.createNewEvent.defaultThankYouMessage', 'Thank you for participating');
}
// Regex that matches placeholder with optional whitespace so we always replace even if HTML has slight variations
const PLACEHOLDER_DATE_RANGE_REGEX = /\{\{\s*DATE_RANGE\s*\}\}/g;
const PLACEHOLDER_TIME_RANGE_REGEX = /\{\{\s*TIME_RANGE\s*\}\}/g;
const PLACEHOLDER_LOCATION_REGEX = /\{\{\s*LOCATION\s*\}\}/g;
// Corrupted placeholders (e.g. {{DATTTTTTE_RANGE}} when user typed into {{DATE_RANGE}}) – normalize to canonical before replace/save
const CORRUPTED_DATE_RANGE_REGEX = /\{\{[^}]*DATE_RANGE[^}]*\}\}/g;
const CORRUPTED_TIME_RANGE_REGEX = /\{\{[^}]*TIME_RANGE[^}]*\}\}/g;
const CORRUPTED_LOCATION_REGEX = /\{\{[^}]*LOCATION[^}]*\}\}/g;
function normalizePlaceholders(html) {
    return html
        .replace(CORRUPTED_DATE_RANGE_REGEX, PLACEHOLDER_DATE_RANGE)
        .replace(CORRUPTED_TIME_RANGE_REGEX, PLACEHOLDER_TIME_RANGE)
        .replace(CORRUPTED_LOCATION_REGEX, PLACEHOLDER_LOCATION);
}
/**
 * Repair broken paragraph tags:
 * 1) "p class=\"ql-align-center\">" missing the leading "<" -> "<p class=..."
 * 2) "</p><p" corrupted to "<p" (stray "<" after time e.g. "18:00<") -> "18:00</p><p"
 */
export function fixBrokenParagraphTags(html) {
    if (!html || typeof html !== 'string')
        return html;
    let out = html.replace(/(^|>)p (class="|style=")/g, '$1<p $2');
    // Fix corrupted "</p><p" that became "<p" (e.g. after "18:00") so stray "<" disappears
    out = out.replace(/(:\d{2})<p>/g, '$1</p><p>').replace(/(:\d{2})<p /g, '$1</p><p ');
    return out;
}
/** Strip HTML tags and return plain text. */
function getTextContent(html) {
    return html
        .replace(/<[^>]+>/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}
/** True when HTML includes styling beyond a plain unstyled paragraph wrapper. */
export function hasRichTextFormatting(html) {
    if (!html || typeof html !== 'string')
        return false;
    return (/<(strong|em|u|s|span|h[1-6]|a)\b/i.test(html) ||
        /\b(style|class)=/i.test(html) ||
        /<(?:p|div|span)\s+[^>]*>/i.test(html));
}
/**
 * Heuristic: paragraph looks like a date-range slot (e.g. "February 12, 2026 - March 10, 2026").
 * Used so we can treat old literal dates as the date slot and show the current date.
 */
function looksLikeDateRangeParagraph(text) {
    const t = text.trim();
    if (t.length > 80 || t.length < 5)
        return false;
    return (/.*\d{4}.*\s-\s.*\d{4}.*/.test(t) ||
        /^\d{1,2}\s*\/\s*\d{1,2}\s*\/\s*\d{4}\s*-\s*\d{1,2}\s*\/\s*\d{1,2}\s*\/\s*\d{4}$/.test(t));
}
/**
 * Replaces {{DATE_RANGE}}, {{TIME_RANGE}}, {{LOCATION}} in HTML with the given values.
 * Normalizes corrupted placeholders (e.g. {{DATTTTTTE_RANGE}}) so they are replaced correctly.
 * Also treats any paragraph that looks like a date range (e.g. old literal date) as the date slot
 * so the current date is shown when the user changes Event Duration.
 */
export function replaceWelcomePlaceholders(html, values) {
    if (!html || typeof html !== 'string')
        return html;
    let result = fixBrokenParagraphTags(html);
    result = normalizePlaceholders(result);
    // Treat paragraphs that are only a date-range-like string as the date slot (fixes old literal dates)
    result = result.replace(/<p([^>]*)>([\s\S]*?)<\/p>/g, (match, pAttrs, content) => {
        const text = getTextContent(content);
        if (looksLikeDateRangeParagraph(text))
            return '<p' + pAttrs + '>' + PLACEHOLDER_DATE_RANGE + '</p>';
        return match;
    });
    const dateRange = values.dateRange != null && String(values.dateRange).trim() !== '' ? values.dateRange : '';
    const timeRange = values.timeRange != null && String(values.timeRange).trim() !== '' ? values.timeRange : '';
    const location = values.location != null && String(values.location).trim() !== '' ? values.location : '';
    result = result
        .replace(PLACEHOLDER_DATE_RANGE_REGEX, dateRange)
        .replace(PLACEHOLDER_TIME_RANGE_REGEX, timeRange)
        .replace(PLACEHOLDER_LOCATION_REGEX, location);
    result = result
        .replace(/<p[^>]*>\s*(?:<br\s*\/?>)?\s*<\/p>/gi, '')
        .replace(/<p><\/p>/g, '')
        .replace(/<p>\s*<\/p>/g, '');
    return result;
}
/** Escape special regex characters in a string. */
function escapeForRegex(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
/**
 * Replace value with placeholder only when the value is the entire content of a
 * paragraph. Allows optional inline tags (e.g. <span>) so Quill-wrapped content matches.
 * Avoids replacing substrings (e.g. "Isl" in "I like Islamabad").
 * Preserves the opening <p> tag and its attributes (e.g. style="text-align: center").
 */
function safeReplaceWithPlaceholder(html, value, placeholder) {
    const s = value != null ? String(value).trim() : '';
    if (s === '')
        return html;
    // Match <p...> optional-whitespace optional-open-tags optional-whitespace value optional-whitespace optional-close-tags </p>
    const re = new RegExp('(<p[^>]*>)(\\s*(?:<[^>]+>)*\\s*)' + escapeForRegex(s) + '(\\s*(?:</[^>]+>)*\\s*)(</p>)');
    return html.replace(re, '$1$2' + placeholder + '$3$4');
}
/**
 * Insert a new paragraph before the `<p` tag that contains `beforePlaceholder`.
 * Falls back to appending at the end if the placeholder is not found.
 */
function insertParagraphBefore(html, newParagraph, beforePlaceholder) {
    const idx = html.indexOf(beforePlaceholder);
    if (idx === -1)
        return html + newParagraph;
    const pStart = html.lastIndexOf('<p', idx);
    if (pStart === -1)
        return html + newParagraph;
    return html.slice(0, pStart) + newParagraph + html.slice(pStart);
}
export function displayToTemplate(html, values) {
    if (!html || typeof html !== 'string')
        return html;
    let out = fixBrokenParagraphTags(html);
    out = normalizePlaceholders(out);
    out = safeReplaceWithPlaceholder(out, values.dateRange, PLACEHOLDER_DATE_RANGE);
    out = safeReplaceWithPlaceholder(out, values.timeRange, PLACEHOLDER_TIME_RANGE);
    out = safeReplaceWithPlaceholder(out, values.location, PLACEHOLDER_LOCATION);
    const hasDatePlaceholder = out.includes(PLACEHOLDER_DATE_RANGE);
    const hasTimePlaceholder = out.includes(PLACEHOLDER_TIME_RANGE);
    const hasLocationPlaceholder = out.includes(PLACEHOLDER_LOCATION);
    // Re-insert missing placeholders in canonical order: Location → Date → Time
    // WITH THIS:
    function findFirstNonPlaceholderPara(html) {
        const paraRegex = /<p[^>]*>[\s\S]*?<\/p>/g;
        let match;
        while ((match = paraRegex.exec(html)) !== null) {
            if (!match[0].includes(PLACEHOLDER_DATE_RANGE) &&
                !match[0].includes(PLACEHOLDER_TIME_RANGE) &&
                !match[0].includes(PLACEHOLDER_LOCATION)) {
                return match.index;
            }
        }
        return -1;
    }
    function insertBeforeUserContent(html, newParagraph) {
        const idx = findFirstNonPlaceholderPara(html);
        if (idx === -1)
            return html + newParagraph;
        return html.slice(0, idx) + newParagraph + html.slice(idx);
    }
    if (!hasLocationPlaceholder &&
        (values.location == null || String(values.location).trim() === '')) {
        const locationPara = `<p>${PLACEHOLDER_LOCATION}</p>`;
        const emptyPara = /<p>\s*<\/p>/;
        if (emptyPara.test(out)) {
            out = out.replace(emptyPara, locationPara);
        }
        else if (hasDatePlaceholder) {
            out = insertParagraphBefore(out, locationPara, PLACEHOLDER_DATE_RANGE);
        }
        else if (hasTimePlaceholder) {
            out = insertParagraphBefore(out, locationPara, PLACEHOLDER_TIME_RANGE);
        }
        else {
            out = insertBeforeUserContent(out, locationPara); // ← was: out += locationPara
        }
    }
    if (!hasDatePlaceholder && (values.dateRange == null || String(values.dateRange).trim() === '')) {
        const datePara = `<p>${PLACEHOLDER_DATE_RANGE}</p>`;
        if (out.includes(PLACEHOLDER_TIME_RANGE)) {
            out = insertParagraphBefore(out, datePara, PLACEHOLDER_TIME_RANGE);
        }
        else {
            out = insertBeforeUserContent(out, datePara); // ← was: out += datePara
        }
    }
    if (!hasTimePlaceholder && (values.timeRange == null || String(values.timeRange).trim() === '')) {
        out = insertBeforeUserContent(out, `<p>${PLACEHOLDER_TIME_RANGE}</p>`); // ← was: out += ...
    }
    return out;
}
/** Returns true if the text contains any welcome placeholder (for conditional UI). */
export function hasWelcomePlaceholders(text) {
    if (!text || typeof text !== 'string')
        return false;
    return (text.includes(PLACEHOLDER_DATE_RANGE) ||
        text.includes(PLACEHOLDER_TIME_RANGE) ||
        text.includes(PLACEHOLDER_LOCATION));
}
